<script setup lang="ts">
import { can } from '@layouts/plugins/casl'
import type { TableOptions } from '@/models/table-options'
import { $getSigns } from '@/api/signs'
import { capitalizeFirstLetters } from '@/helpers/utils/capitalizeFirstLetters'

interface IProps {
  sign?: any,
}

const props = withDefaults(defineProps<IProps>(), {
  sign: null,
})

interface CustomEvents {
}

const emit = defineEmits<CustomEvents>()

const isMounted = ref<boolean>(false)

const showSignReprintDialog = ref<boolean>(false)
const showSignDeletedReprintsDialog = ref<boolean>(false)

const reprints = ref([])
const reprintsLoading = ref<boolean>(false)
const reprintsCount = ref<number>(0)
const reprintHeaders = [
  { title: 'Code', key: 'code', sortable: false },
  { title: 'Reprint', key: 'reprint_type_display', sortable: false },
  { title: 'Cost', key: 'reprint_cost_type_display', sortable: false },
  { title: 'Comment', key: 'reprint_comment', sortable: false },
  { title: 'Info', key: 'reprint_info', sortable: false }
]

const reprintTableOptions = ref<TableOptions>({
  itemsPerPage: 20,
  page: 1,
  sortBy: []
})

const itemsPerPageOptions = [
  { value: 10, title: '10' },
  { value: 15, title: '15' },
  { value: 20, title: '20' },
  { value: 50, title: '50' },
  { value: 100, title: '100' },
]

onMounted(() => {
  getSignReprints()

  isMounted.value = true
})

const getSignReprints = async () => {
  reprintsLoading.value = true
  try {
    const res = await $getSigns({
      reprint_root_sign: props.sign?.id,
      is_removed: false,
      is_reprint: true,
      is_reprint_list: true,
      ordering: 'id',
      per_page: 400
    })
    reprints.value = res.data.results
    reprintsCount.value = res.data.count
  } catch (e: any) {
    // If page doesn't exist goes to 1 page
    if (e.response.status === 404) {
      reprintTableOptions.value.page = 1
      await getSignReprints()
    } else {
      console.log(e)
    }
  } finally {
    reprintsLoading.value = false
  }
}
</script>

<template>
  <VContainer
    fluid
    class="pl-0 py-0"
    style="max-height: 100%;"
  >
    <SignReprintDialog
      v-model="showSignReprintDialog"
      :sign="sign"
      @onSubmit="getSignReprints"
    />
    <SignDeletedReprintsDialog
      v-model="showSignDeletedReprintsDialog"
      :sign="sign"
      @onRestore="getSignReprints"
    />
    <VOverlay
      :model-value="reprintsLoading"
      absolute
      class="align-center justify-center loading w-100 h-100"
      contained
      persistent
    >
      <VProgressCircular
        color="primary"
        indeterminate
      />
    </VOverlay>
    <VRow>
      <VCol>
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div class="d-flex align-center flex-wrap">
            <h4 class="mt-1">
              Total: {{ reprintsCount }}
            </h4>
            <VBtn
              size=""
              color="secondary"
              variant="elevated"
              class="padding-all-1 ml-1"
              @click="showSignDeletedReprintsDialog = true"
            >
              <VIcon icon="tabler-history" />
              <VMenu
                activator="parent"
                location="top center"
                open-on-hover
                open-delay="100"
                close-delay="0"
                offset="1"
                class="default-tooltip"
              >
                  <span class="px-2">
                    Show deleted reprints
                  </span>
              </VMenu>
            </VBtn>
          </div>
          <div
            v-if="can('change', 'sign') && !sign?.is_reprint"
          >
            <VBtn
              :disabled="!sign?.id"
              class="px-1"
              size="small"
              color="default"
              variant="tonal"
              @click="showSignReprintDialog = true"
            >
              Add Reprint
            </VBtn>
          </div>
        </div>
      </VCol>
    </VRow>
    <VRow
      v-if="reprints.length > 0"
      class="mt-1"
    >
      <VCol
        cols="12"
        v-for="(reprint, reprintKey) in reprints"
        :key="reprint.id"
        class="border-b"
      >
        <VRow>
          <VCol
            cols="12"
            class="py-1"
          >
            <h4 class="d-flex align-center justify-center">
              {{ reprint.code }}
              <SignDetailsModal
                :is-sign-reprint-block="true"
                :sign-id="reprint.id"
                @sign-updated="getSignReprints"
              />
            </h4>
          </VCol>
          <VCol
            cols="12"
            class="py-1"
          >
            <VLabel class="text-high-emphasis">
              <b>Reprint Type:</b>
            </VLabel>
            {{ capitalizeFirstLetters(reprint.reprint_type_display) }}
          </VCol>
          <!-- If reprint - FULL -->
          <VCol
            v-if="reprint.reprint_type === 1"
            cols="12"
            sm=""
            class="py-1"
          >
            <VLabel class="text-high-emphasis">
              <b>Artwork:</b>
            </VLabel>
            {{ reprint.reprint_artwork_type_display }}
          </VCol>
          <!-- /If reprint - FULL -->
          <!-- If reprint - PATCH -->
          <VCol
            v-if="reprint.reprint_type === 2"
            cols="12"
            sm=""
            class="py-1"
          >
            <VLabel class="text-high-emphasis">
              <b>Patch:</b>
            </VLabel>
            {{ reprint.reprint_patch_price_type_display }}
          </VCol>
          <!-- /If reprint - PATCH -->
          <VCol
            cols="12"
            class="py-1"
          >
            <VLabel class="text-high-emphasis">
              <b>Cost:</b>
            </VLabel>
            {{ reprint.reprint_cost_type_display }}
          </VCol>
          <VCol
            cols="12"
            class="py-1"
          >
            <VLabel class="text-high-emphasis">
              <b>Comment:</b>
            </VLabel>
            {{ reprint.reprint_comment }}
          </VCol>
          <!-- If reprint - PATCH -->
          <VCol
            v-if="reprint.reprint_type === 2"
            cols="12"
            sm="6"
            class="py-1"
          >
            <VLabel class="text-high-emphasis">
              <b>Width:</b>
            </VLabel>
            {{ reprint.width }}
          </VCol>
          <VCol
            v-if="reprint.reprint_type === 2"
            cols="12"
            sm="6"
            class="py-1"
          >
            <VLabel class="text-high-emphasis">
              <b>Height:</b>
            </VLabel>
            {{ reprint.height }}
          </VCol>
          <!-- /If reprint - PATCH -->
          <VCol
            cols="12"
            class="py-1"
          >
            <VLabel class="text-high-emphasis">
              <b>Status:</b>
            </VLabel>
            {{ reprint.status_name }}
          </VCol>
        </VRow>
      </VCol>
    </VRow>
    <VRow v-else>
      <VCol>
        <h4 class="text-center">
          Reprints not found
        </h4>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style lang="scss" scoped>

</style>
